import { LitElement, html, css, unsafeHTML } from "./lit-all.min.js";

const buttonStyles = css`
	.button {
		padding: 8px 16px;
		border-radius: 2px;
		font: var(--text-md);
		text-decoration: none;
		text-align: center;
		display: inline-flex;
		gap: 8px;
		justify-content: center;
		align-items: center;
		outline: none;
		-webkit-tap-highlight-color: transparent;
		background: none;
		border: none;
		cursor: pointer;
		white-space: nowrap;
	}

	/* Icon */
	.button::before {
		display: block;
		width: 24px;
		height: 24px;
		background-size: 100%;
		background-repeat: no-repeat;
	}

	@media screen and (min-width: 768px) {
		.button {
			font: var(--text-lg);
		}
	}

	.button[disabled] {
		cursor: not-allowed;
	}

	.button--primary {
		background-color: var(--color-button-bg-primary);
		color: var(--color-button-text-primary) !important;
	}

	.button--primary.button--inverted {
		background-color: var(--color-button-bg-secondary);
		color: var(--color-text-primary) !important;
	}

	.button--secondary {
		background-color: var(--color-button-bg-secondary);
		border: 1px solid var(--color-border-button-primary);
		color: var(--color-text-primary) !important;
	}

	.button--secondary.button--inverted {
		background: none;
		color: var(--color-text-inverted-primary, #FFF) !important;
		border: 1px solid var(--color-border-icon-inverted-primary, #FFF);
	}

	.button--tertiary {
		border: none;
		text-decoration: underline;
		text-decoration-color: var(--color-button-text-tertiary);
		padding: var(--padding-xs) 0px;
		color: var(--color-button-text-tertiary)
	}

	.button--tertiary.button--inverted {
		color: var(--color-text-inverted-primary, #FFF) !important;
		text-decoration-color: var(--color-text-inverted-primary, #FFF) !important;
	}

	.button--link {
		border: none;
		background: none;
	}

	.enable-design-system .button--secondary {
		border: 1px solid var(--color-border-button-primary, #BDBDBD);
	}

	@media screen and (hover: hover) {
		.button--primary:hover,
		.button--secondary:hover,
		.button--tertiary:hover,
		.button--link:hover {
			opacity: 0.8;
		}
	}

	.button--primary:active {
		background-color: #292929;
	}

	.button--secondary:active,
	.button--tertiary:active {
		background-color: var(--button-bg-active);
	}

	.button--link:active {
		opacity: 0.5;
	}

	.button--primary[disabled] {
		opacity: 0.5;
	}

	.button--secondary[disabled] {
		background: var(--color-surface-surface-tertiary, #EBEBEB); 
		border: 1px solid var(--color-surface-surface-tertiary, #EBEBEB);
		color: var(--color-text-text-disabled, #949494) !important; 
	}

	.button--error {
		color: red;
		border: 1px solid red;
	}

	/* Loading overlay */
	.show-loading {
		position: relative;
		color: transparent !important;
		pointer-events: none;
	}

	.show-loading::after {
		content: "";
		position: absolute;
		height: 16px;
		width: 16px;
		left: calc(50% - 8px);
		top: calc(50% - 8px);
		opacity: 60%;
		animation: show-loading-anim 1s linear infinite;
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M222.7 32.1c5 16.9-4.6 34.8-21.5 39.8C121.8 95.6 64 169.1 64 256c0 106 86 192 192 192s192-86 192-192c0-86.9-57.8-160.4-137.1-184.1c-16.9-5-26.6-22.9-21.5-39.8s22.9-26.6 39.8-21.5C434.9 42.1 512 140 512 256c0 141.4-114.6 256-256 256S0 397.4 0 256C0 140 77.1 42.1 182.9 10.6c16.9-5 34.8 4.6 39.8 21.5z'/%3E%3C/svg%3E");
	}

	.show-loading--inverted::after {
		filter: invert(1);
	}

	@keyframes show-loading-anim {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}
`;

class BundleCompetition extends LitElement {
	static properties = {
		title: { type: String },
		subtitle: { type: String },
		image: { type: String },
		buttonLabel: { type: String },
		listId: { type: String },
		thankyouTitle: { type: String },
		thankyouSubtitle: { type: String },
		thankyouButtonLabel: { type: String },
		thankyouButtonLink: { type: String },
		terms: { type: String },
		error: { type: String },
		items: { type: Array },
	}

	constructor() {
		super();

		this.title = 'Subscribe and compete for a free cotton bundle';
		this.subtitle = '<p>We’ll add your bundle to the wishlist and announce the winner on July 15th. The more bundles you add, the more chances you have to win!</p>';
		this.buttonLabel = 'Subscribe & Win';
		this.image = 'https://bedthreads.com.au/cdn/shop/files/Landing-BYOB-Banner-small.jpg?v=1751264848&width=1200';
		this.listId = '';

		this.thankyouTitle = 'Thank you for participating on the contest!';
		this.thankyouSubtitle = '<p>We’ll announce the winners on cotton launch day: July 15th. Good luck!</p>';
		this.thankyouButtonLabel = 'Explore more Cotton items';
		this.thankyouButtonLink = '/collections/bedding';

		this.terms = '<p>By entering the competition, you agree to receive marketing emails from Bed Threads. You can unsubscribe at any time.</p>';

		this.error = '';
		this.items = [];

		window.addEventListener('hashchange', () => {
			if (window.location.hash === '#bundle-competition-terms') this.openTerms();
		});
	}

	connectedCallback() {
		super.connectedCallback();
	}

	willUpdate(changedProperties) {
		if (changedProperties.has('items') && JSON.stringify(this.items) != JSON.stringify(changedProperties.get('items')) && this.shadowRoot.querySelector('.content')) {
			this.shadowRoot.querySelector('.content.form').style.display = '';
			this.shadowRoot.querySelector('.content.thankyou').style.display = 'none';
		}
	}

	open() {
		const render = document.querySelector('byob-render');

		if (render && render.createScreenshot) {
			const image = render.createScreenshot(750, 600);
			this.image = image;
		}

		this.shadowRoot.querySelector('#competition-modal').open();
	}

	openTerms() {
		this.shadowRoot.querySelector('#terms-modal').open();
		if (window.location.hash === '#bundle-competition-terms') {
			window.location.hash = '';
		}
	}

	async submit(e) {
		this.error = '';

		const button = e.target;
		button.classList.add('show-loading', 'show-loading--inverted');

		const emailInput = this.shadowRoot.querySelector('#email');

		if (!emailInput.checkValidity()) {
			this.error = 'Please enter a valid email address.';
			e.target.classList.remove('show-loading', 'show-loading--inverted');
			return;
		}

		const bundleUrl = window.bundleBuilder.getConfigUrl();
		const endpoint = window.location.hostname.includes('127.0.0.1') ? 'http://127.0.0.1:8787/' : 'https://bundle-competition.bedthreads.workers.dev/';

		const render = document.querySelector('byob-render');
		const portraitImage = render && render.createScreenshot ? render.createScreenshot(800, 1000) : null;

		const payload = {
			shop: window.Shopify.shop,
			email: emailInput.value,
			data: {
				image: this.image,
				portraitImage: portraitImage,
				url: bundleUrl,
				items: this.items,
			},
			listId: this.listId,
		};
		
		try {
			const resp = await fetch(endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(payload)
			});

			if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
		}
		catch (error) {
			this.error = error;
			button.classList.remove('show-loading', 'show-loading--inverted');
			return;
		}

		this.shadowRoot.querySelector('.content.form').style.display = 'none';
		this.shadowRoot.querySelector('.content.thankyou').style.display = '';
		button.classList.remove('show-loading', 'show-loading--inverted');
	}

	render() {
		return html`
			<slide-out-modal id="competition-modal">
				<div class="content form">
					<h2>${this.title}</h2>
					<div>${unsafeHTML(this.subtitle)}</div>
					<img src="${this.image}" />
					<div class="input-group">
						<label for="email">Email</label>
						<input type="email" name="email" id="email" required />
						${this.error ? html`<div class="error">${this.error}</div>` : ''}
					</div>
					<button class="button button--primary" @click=${this.submit}>${this.buttonLabel}</button>
					<button class="terms-button button button--tertiary" @click=${() => this.openTerms()}>Terms & Conditions</button>
				</div>

				<div class="content thankyou" style="display: none;">
					<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M12.5 22C18.0228 22 22.5 17.5228 22.5 12C22.5 6.47715 18.0228 2 12.5 2C6.97715 2 2.5 6.47715 2.5 12C2.5 17.5228 6.97715 22 12.5 22Z" stroke="#212121" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M7.5 12.5L10.5 15.5L17.5 8.5" stroke="#212121" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
					<h2>${this.thankyouTitle}</h2>
					<div>${unsafeHTML(this.thankyouSubtitle)}</div>
					<a class="button button--primary" href="${this.thankyouButtonLink}" target="_blank">${this.thankyouButtonLabel}</a>
					<button class="terms-button button button--tertiary" @click=${() => this.openTerms()}>Terms & Conditions</button>
				</div>
			</slide-out-modal>

			<slide-out-modal id="terms-modal">
				<div class="content terms">
					<h2>Terms & Conditions</h2>
					<div class="description">
						<div class="scroll">${unsafeHTML(this.terms)}</div>
					</div>
					<button class="button button--primary" @click="${() => this.shadowRoot.querySelector('#terms-modal').close()}">Got it!</button>
				</div>
			</slide-out-modal>
		`;
	}

	static styles = css`
		${buttonStyles}

		* {
			margin: 0;
			box-sizing: border-box;
		}

		.content {
			display: flex;
			flex-direction: column;
			gap: var(--padding-xs);
			max-width: 400px;
		}

		@media screen and (min-width: 992px) {
			.content {
				min-width: 400px;
			}
		}

		svg {
			align-self: center;
			display: block;
		}

		h2 {
			font: var(--title-sm);
			text-align: center;
			color: var(--color-text-primary);
		}

		p {
			font: var(--text-md);
			color: var(--color-text-tertiary);
			text-align: center;
		}

		p:not(:last-child) {
			margin-bottom: var(--padding-xs);
			color: var(--color-text-primary);
		}

		.terms h5 {
			font: var(--text-lg);
			color: var(--color-text-primary);
			margin-bottom: var(--padding-xs);
			margin-top: var(--padding-sm);
		}

		.terms p {
			text-align: left;
		}

		.terms .description {
			position: relative;
		}

		.terms .description::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: var(--padding-md);
			background: linear-gradient(to top, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
			pointer-events: none;
		}

		.terms .description::after {
			content: '';
			position: absolute;
			bottom: 0;
			left: 0;
			width: 100%;
			height: var(--padding-md);
			background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
			pointer-events: none;
		}

		.terms .scroll {
			overflow-y: auto;
			max-height: 75vh;
			max-height: 75dvh;
			padding-block: var(--padding-sm);
		}

		img {
			display: block;
			width: 100%;
			height: auto;
			aspect-ratio: 3/2;
			object-fit: cover;
			object-position: center 70%;
		}

		.input-group {
			display: flex;
			flex-direction: column;
			gap: 4px;
		}

		input {
			font: var(--text-md);
			border-radius: var(--spacing-radious-radius-sm, 2px);
			border: 1px solid var(--color-border-button-primary, #BDBDBD);
			background: var(--color-button-bg-secondary, #FFF);
			width: 100%;
			padding: var(--padding-xs) var(--padding-sm);
		}

		label {
			font: var(--text-md);
		}

		.error {
			color: red;
			font: var(--text-sm);
		}

		.button {
			margin-top: var(--padding-xs);
		}

		.form .button--primary {
			margin-top: 0;
		}

		.button--primary {
			font-size: 16px;
		}

		.terms-button {
			font: var(--text-sm);
		}
	`
}

customElements.define("bundle-competition", BundleCompetition);